import {
  SegmentActionName,
  SegmentObjectName,
  track,
  TrackingEventName,
  trackLegacyEvent,
  type EventPropsOf,
} from '@heidi/analytics/events';
import { type TemplateAuthorGroup } from '@heidi/schema/ml-scribe';

export function templateCreateAskAi(payload: { aiCommandText: string }) {
  // Fires when AI content finishes streaming — intent signal only; the
  // authoritative `template_created` fires at save time.
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateCreatedUseAskAi,
      aiCommandText: payload.aiCommandText,
    },
  });
}

export function trackTemplateEditorUpdated({
  templateId,
  tabType,
  version,
}: {
  templateId?: string;
  tabType: 'example' | 'structure';
  version: 'v1' | 'v2';
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateEditorUpdated,
    props: {
      template_id: templateId,
      tab_type: tabType,
      template_editor_version: version,
    },
  });
}

export function trackTemplateLibraryFilteredApplied(filter: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibraryFilteredApplied,
      filter,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: filter,
      search_action: 'filter_applied',
    },
  });
}

export function trackTemplateLibrarySearched(searchText: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibrarySearched,
      searchQuery: searchText,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: searchText,
      search_action: 'searched',
    },
  });
}

export function trackTemplateLibrarySortApplied(sort: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibrarySortApplied,
      sort,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: sort,
      search_action: 'sort_applied',
    },
  });
}

export function trackStructuredFormTemplateUpdated(
  props: EventPropsOf<TrackingEventName.TemplateUpdated>
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props,
  });
}

export function trackTemplateGeneration({
  templateId,
  src,
  useSections,
  verifyCompliance,
}: {
  templateId?: string;
  src: 'chat' | 'example' | 'structure';
  useSections?: boolean;
  /** True only when the Fix for me button sent the chat request. */
  verifyCompliance?: boolean;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateGeneration,
    props: {
      template_id: templateId,
      src,
      use_sections: useSections,
      verify_compliance: verifyCompliance,
    },
  });
}

export function trackTemplateOpened({
  templateId,
  templateAuthorGroup,
  version,
}: {
  templateId: string;
  templateAuthorGroup?: TemplateAuthorGroup | null;
  version: 'v1' | 'v2';
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateOpened,
    props: {
      template_id: templateId,
      template_author_group: templateAuthorGroup,
      template_editor_version: version,
    },
  });
}

export function trackTemplateTypeFilterApplied() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: 'template_type',
      search_action: 'filter_applied',
    },
  });
}
